import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Modal } from "../ui";

/**
 * The prompt that stands between a half-filled form and the rail.
 *
 * ## Why this is not `useBlocker`
 *
 * React Router ships one — and it only works under a data router. The app
 * mounts `<BrowserRouter>` (see `App`), so there is no navigation blocking to
 * hook into, and switching the whole route table over to `createBrowserRouter`
 * to guard four forms is a great deal of risk for a dialog.
 *
 * So the guard sits where the navigation is actually started rather than
 * underneath the router: the rail asks before it calls `navigate`, and a tab
 * strip that swaps one form for another asks the same way before it switches
 * (see `MyBrandPage`). That is narrower on purpose and worth being plain
 * about — what is guarded is the sidebar and the tab strip, which are the
 * ways somebody leaves a form by accident. A breadcrumb, the form's own Cancel
 * and the browser's back button are all deliberate exits and go straight
 * through, and a closed tab is the browser's to ask about.
 *
 * ## What it is not
 *
 * Not an autosave. Nothing on these screens is kept as a draft, which is what
 * every one of the messages below has to land: the choice is finish and save,
 * or lose what was typed. A prompt that implies otherwise is worse than none.
 *
 * Which is also why the confirm button saves rather than merely closing. It is
 * labelled in the words of the screen's own Save row — "Save changes", "Save &
 * add" — and a button that says that and does nothing but put you back in front
 * of the form is the same broken promise the paragraph above is about. So it
 * does what it says: sends the form, and carries on to wherever the rail was
 * pointing once it has landed. See `UnsavedAnswer`.
 */

/**
 * What one screen's prompt says.
 *
 * Written by the screen rather than by this file. A dialog that says "unsaved
 * changes" over any form is the kind people dismiss without reading — naming
 * the thing at risk, in the words that screen already uses, is most of what
 * makes somebody stop. So the copy travels with the form that has something to
 * lose, and the shell only decides when to show it.
 */
export interface UnsavedPrompt {
  /** Heading, and the dialog's accessible name — "Unsaved client details". */
  title: string;
  /** What is unsaved, and what leaving costs. Two sentences, both plain. */
  body: string;
  /**
   * The label on the button that saves the form — "Save & add", "Setup brand".
   *
   * Worded as the thing left undone rather than as "Keep editing": the answer
   * this dialog wants is that the form gets finished, so the button names what
   * finishing is on that screen — and then does it.
   */
  confirmLabel: string;
}

/**
 * What the confirm button does, read at the moment it is pressed rather than
 * registered with the copy — see `useUnsavedChanges`.
 *
 * Every one of these forms already knows how to answer its own Save row, so
 * this is that same answer handed up: whether the form can be sent as it
 * stands, how to send it, and how to say what is missing when it cannot.
 */
export interface UnsavedAnswer {
  /**
   * Whether the form is complete enough to send. False puts the press through
   * to `report` instead, which is exactly what the form's own Save row does
   * while something required is missing.
   */
  canSave: boolean;
  /**
   * Sends the form, and resolves to whether all of it landed. `false` leaves
   * the user on the screen — a save that failed has already said so in a toast,
   * and the details are still in the boxes to be sent again.
   *
   * Must not navigate: where to go next is the held navigation's, and a screen
   * that also moved would take the user somewhere they did not ask for.
   */
  save: () => Promise<boolean>;
  /** Marks up what is missing, for a press this dialog cannot honour. */
  report: () => void;
  /**
   * Throws the draft away — what the screen's own Cancel does.
   *
   * Needed by a form that stays mounted past the navigation it was asked
   * about. A rail press unmounts the page and the draft with it, so the forms
   * behind the rail can leave this out. A tab switch does not: the screen
   * keeps its state across it, and a draft merely let go of would still be in
   * the boxes when the tab was next opened — and, with its entry taken down
   * here, no longer asked about.
   */
  discard?: () => void;
}

/** One form's registration: what its prompt says, and what its buttons do. */
interface Registration {
  prompt: UnsavedPrompt;
  /** Called on the press, so it answers with the form as it stands then. */
  answer: () => UnsavedAnswer;
}

interface UnsavedChangesValue {
  /**
   * Registers or clears one form's unsaved state, keyed so a form leaving the
   * screen takes down its own entry and nobody else's. Called by
   * `useUnsavedChanges`; no screen should reach for it directly.
   */
  setDirty: (
    id: string,
    dirty: boolean,
    prompt: UnsavedPrompt,
    answer: () => UnsavedAnswer
  ) => void;
  /**
   * Runs `go` now if there is nothing to lose, or holds it behind the prompt if
   * there is. Called by the rail — see `useNavigationGuard`.
   */
  confirmNavigation: (go: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesValue | null>(null);

/**
 * What a guarded navigation does where there is no provider above it: happen.
 *
 * A module constant rather than an inline arrow, so the hook below returns the
 * same function every render and callers can depend on it. Every screen has the
 * provider in real use — it is part of the shell — but a page rendered on its
 * own in a test has none, and a guard that threw there would turn an absent
 * dialog into a failing suite.
 */
const runNow = (go: () => void) => go();

/** A held navigation: where it was going, and what to ask before it does. */
interface PendingNavigation {
  go: () => void;
  /** Which form is being asked about, so only its entry comes down after. */
  id: string;
  prompt: UnsavedPrompt;
  answer: () => UnsavedAnswer;
}

export const UnsavedChangesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  /**
   * Which forms currently hold something unsaved, and what each would say.
   *
   * A map rather than a boolean, and a ref rather than state. The map because
   * an unmounting form must clear its own entry and not the flag itself — only
   * one form is open at a time today, but a screen that grows a second one
   * should not silently turn the guard off. The ref because nothing on screen
   * is drawn from it: it is read at the moment a rail item is pressed, so
   * re-rendering the whole shell on every keystroke inside a form would buy
   * nothing.
   */
  const dirty = useRef(new Map<string, Registration>());

  /** The navigation being asked about, or `null` when nothing is. */
  const [pending, setPending] = useState<PendingNavigation | null>(null);

  /** True from the press of the confirm button until the save answers. */
  const [saving, setSaving] = useState(false);

  const setDirty = useCallback(
    (
      id: string,
      isDirty: boolean,
      prompt: UnsavedPrompt,
      answer: () => UnsavedAnswer
    ) => {
      if (isDirty) dirty.current.set(id, { prompt, answer });
      else dirty.current.delete(id);
    },
    []
  );

  const confirmNavigation = useCallback((go: () => void) => {
    // The copy is taken now rather than held in state as it changes: what the
    // dialog says is whatever the screen was saying at the moment the rail was
    // pressed, and it must not shift underneath somebody reading it. The
    // answer behind the buttons is a reader rather than a value, for the
    // opposite reason — see `Registration`.
    const [entry] = Array.from(dirty.current.entries());
    if (!entry) {
      go();
      return;
    }

    const [id, registration] = entry;
    setPending({ go, id, ...registration });
  }, []);

  const value = useMemo(
    () => ({ setDirty, confirmNavigation }),
    [setDirty, confirmNavigation]
  );

  /**
   * The form that was asked about is answered for: its entry comes down, and
   * the held navigation is put through the guard once more.
   *
   * Taken down here rather than left to the form's own unmount. The unmount
   * does take it down, but not until React has torn the page down, and the
   * entry must be gone before the next press reads the map — otherwise a
   * second item pressed straight after this one prompts again about a form
   * that is already leaving. And a form kept mounted across a tab switch never
   * unmounts at all.
   *
   * Its entry alone, and then the guard again, rather than the whole map
   * cleared and the navigation made: a screen holding two drafts at once — My
   * brand's details and its guidelines — is asked about each in turn, and
   * the second is not lost on the strength of an answer about the first.
   */
  const release = ({ id, go }: PendingNavigation) => {
    dirty.current.delete(id);
    confirmNavigation(go);
  };

  /**
   * Back to the form — the prompt goes and nothing typed is touched.
   *
   * This is Escape's answer, and the safe one. Shut while a save is with the
   * server: the dialog is reporting work in flight by then, and closing it
   * would leave that work unannounced.
   */
  const stay = () => {
    if (!saving) setPending(null);
  };

  /**
   * The confirm button: what the screen's own Save row would do, done from
   * here — and then on to wherever the rail was pointing.
   *
   * A form that cannot be sent yet is marked up instead, which is the same
   * answer its Save row gives while something required is missing. Nothing is
   * navigated in that case: the fields that need filling in are on the screen
   * behind this dialog, and leaving would take them off it.
   */
  const saveAndGo = async () => {
    if (!pending || saving) return;

    const { canSave, save, report } = pending.answer();

    if (!canSave) {
      setPending(null);

      // Marked up once the prompt is really gone. The dialog hands focus back
      // to whatever opened it as it unmounts, so pointing at the failing field
      // any earlier would have the cursor pulled straight back out of it.
      setTimeout(report, 0);
      return;
    }

    setSaving(true);
    const stored = await save();
    setSaving(false);
    setPending(null);

    // A save that did not land has already said why, and every answer is still
    // in the boxes on the screen behind this — which is the only place they can
    // be corrected and sent again.
    if (!stored) return;

    release(pending);
  };

  const discard = () => {
    if (!pending) return;
    setPending(null);

    // The screen's own Cancel, where the screen has one to give — see
    // `UnsavedAnswer`. Before the entry comes down, so a form that reverts
    // and re-registers as clean is not racing the map.
    pending.answer().discard?.();
    release(pending);
  };

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}

      {/* Not dismissable by a click beside it: this is a dialog asking
          something, with two real answers in it, and a stray click is far more
          likely to be a miss than a decision. Escape still closes it, and lands
          on the safe answer — the form stays open and nothing is lost. */}
      <Modal
        open={pending !== null}
        title={pending?.prompt.title ?? ""}
        onClose={stay}
        dismissOnBackdrop={false}
        // The heading is drawn in the body below rather than by the modal's own
        // bar — see there. `title` is still passed and still does its job: with
        // `hideTitle` the `h2` stays in the tree, off screen, as the dialog's
        // accessible name.
        hideTitle
        className="max-w-[500px]"
        footer={
          <>
            {/* Neutral rather than red. Discarding is destructive, but this
                dialog is already the warning — the panel exists to say what is
                about to be lost, and a red button inside it marks the same
                thing twice. What separates the two answers here is weight: the
                filled one is what the dialog is recommending.

                Off while the other answer is with the server: throwing the
                form away half way through storing it is not an answer to
                anything. */}
            <Button
              variant="secondary"
              size="md"
              onClick={discard}
              disabled={saving}
            >
              Discard
            </Button>
            {/* The answer the dialog wants, in the words of the screen that
                asked — and it does what it says. See `UnsavedAnswer`. */}
            <Button
              variant="primary"
              size="md"
              onClick={saveAndGo}
              disabled={saving}
            >
              {saving ? "Saving…" : pending?.prompt.confirmLabel}
            </Button>
          </>
        }
      >
        {/* Heading and rule drawn here rather than taken from the modal's title
            bar, which runs its border the full width of the panel. This one is
            inset to the panel's padding, so the rule starts and ends with the
            text it separates.

            Hidden from screen readers: the `h2` above is already carrying this
            same string as the dialog's name, and leaving both readable would
            announce the title twice. */}
        <h3
          aria-hidden="true"
          className="text-[20px] 3xl:text-[22px] font-medium text-ink"
        >
          {pending?.prompt.title}
        </h3>
        <div className="mt-4 border-t border-line" />

        <p className="mt-4 text-[14px] 3xl:text-[15px] leading-[1.6] text-ink-muted">
          {pending?.prompt.body}
        </p>
      </Modal>
    </UnsavedChangesContext.Provider>
  );
};

/**
 * Tells the shell this screen is holding something that is not saved, and what
 * to say about it.
 *
 * Call it unconditionally at the top of the page, with whatever the screen
 * already knows about being dirty — every one of these forms keeps the record
 * it opened with, so the answer is a comparison it can make itself rather than
 * anything new to track.
 *
 * The flag lifts on unmount, so a form that saved and navigated away leaves
 * nothing behind: the page is gone before anyone can be asked about it.
 *
 * `answer` is what the prompt's confirm button does — the screen's own Save,
 * handed up. Written as an inline literal like the prompt; nothing about it has
 * to be stable.
 */
export const useUnsavedChanges = (
  dirty: boolean,
  prompt: UnsavedPrompt,
  answer: UnsavedAnswer
) => {
  const context = useContext(UnsavedChangesContext);

  // Stable for the life of the component, which is what makes the entry the
  // form's own — and what lets the cleanup below remove exactly it.
  const id = useId();

  // Depended on one field at a time rather than as an object. Callers pass the
  // prompt as an inline literal — a fresh object every render — so an effect
  // depending on it would register again on every keystroke inside the form.
  const { title, body, confirmLabel } = prompt;

  /**
   * The answer as it stands, read rather than registered.
   *
   * Every part of it moves as the form is typed into — `canSave` flips on the
   * keystroke that completes a required field, and both callbacks close over
   * the answers as they are now. Registering it would put a fresh entry in the
   * shell's map for every character; reading it at the press instead keeps the
   * entry stable and what it does current, which is the pair that matters.
   */
  const latest = useRef(answer);
  useEffect(() => {
    latest.current = answer;
  });
  const readAnswer = useCallback(() => latest.current, []);

  useEffect(() => {
    if (!context) return;

    const registered = { title, body, confirmLabel };
    context.setDirty(id, dirty, registered, readAnswer);
    return () => context.setDirty(id, false, registered, readAnswer);
  }, [context, id, dirty, title, body, confirmLabel, readAnswer]);
};

/**
 * The rail's way of leaving: hand it the navigation rather than doing it, and
 * it either happens or is asked about first.
 */
export const useNavigationGuard = () =>
  useContext(UnsavedChangesContext)?.confirmNavigation ?? runNow;
